import type Block from 'quill/blots/block';
import { BlockEmbed } from 'quill/blots/block';
import Quill from 'quill/core';
import type { BlockEmbedMovedInOptions } from '../types';
import findQuillByBlot from './findQuillByBlot';

function moveCaretIntoBlockEmbedWithKeyboard(
  blockEmbed: BlockEmbed,
  movedInOptions: BlockEmbedMovedInOptions,
) {
  const quill = findQuillByBlot(blockEmbed);
  if (!quill) return;

  if (document.activeElement !== quill.root) {
    // Explicitly focus on Quill to ensure keyboard shortcuts work after
    // the selection is moved to a block embed.
    // Otherwise, the focus might remain inside the nested editor.
    quill.root.focus({ preventScroll: true });
  }

  const index = blockEmbed.offset(blockEmbed.scroll);
  quill.setSelection(index, 1, Quill.sources.USER);

  if (blockEmbed.onCaretMovedInWithKeyboard) {
    blockEmbed.onCaretMovedInWithKeyboard(movedInOptions);
  }
}

/**
 * Returns a Quill index at a vertical edge of a specific block, based on a given x-offset.
 *
 * The function refrains from using `document.caretRangeFromPoint` to account for scenarios
 * where other elements (like toolbars) are overlaying the block.
 */
const getIndexAtBlockEdgeFromXOffset = (
  quill: Quill,
  block: Block,
  {
    direction,
    xOffset,
  }: Extract<
    BlockEmbedMovedInOptions,
    { direction: 'up' } | { direction: 'down' }
  >,
) => {
  const length = block.length();
  const lineOffset = block.offset(block.scroll);

  if (direction === 'up') {
    let lastLeft = Infinity;
    for (let i = length - 1; i >= 0; i -= 1) {
      const index = lineOffset + i;
      const bounds = quill.selection.getBounds(index);
      if (!bounds) return index;
      if (bounds.left > lastLeft) return index + 1;
      if (bounds.left <= xOffset) {
        return Math.abs(xOffset - bounds.left) > Math.abs(xOffset - lastLeft)
          ? index + 1
          : index;
      }
      lastLeft = bounds.left;
    }
    return lineOffset;
  }

  let lastLeft = -Infinity;
  for (let i = 0; i < length; i += 1) {
    const index = lineOffset + i;
    const bounds = quill.selection.getBounds(index);
    if (!bounds) return index;
    if (bounds.left < lastLeft) return index - 1;
    if (bounds.left >= xOffset) {
      return Math.abs(xOffset - bounds.left) > Math.abs(xOffset - lastLeft)
        ? index - 1
        : index;
    }
    lastLeft = bounds.left;
  }
  return lineOffset + length - 1;
};

const moveCaretIntoBlockWithKeyboard = (
  block: Block,
  movedInOptions: BlockEmbedMovedInOptions,
) => {
  const quill = findQuillByBlot(block);
  if (!quill) return;

  switch (movedInOptions.direction) {
    case 'left':
      quill.setSelection(
        block.offset(block.scroll) + block.length() - 1,
        0,
        Quill.sources.USER,
      );
      break;
    case 'right':
      quill.setSelection(block.offset(block.scroll), 0, Quill.sources.USER);
      break;
    default:
      quill.setSelection(
        getIndexAtBlockEdgeFromXOffset(quill, block, movedInOptions),
        0,
        Quill.sources.USER,
      );
  }
};

export default function moveCaretIntoLineWithKeyboard(
  line: Block | BlockEmbed,
  movedInOptions: BlockEmbedMovedInOptions,
) {
  if (line instanceof BlockEmbed) {
    moveCaretIntoBlockEmbedWithKeyboard(line, movedInOptions);
  } else {
    moveCaretIntoBlockWithKeyboard(line, movedInOptions);
  }
}
